/**
 * <machine-viewer> — the model viewer page, /pages/model-viewer (request 193,
 * 2026-10-26): a version of the garage, and for the chosen machine every
 * assembly the Fitment API has a drawing for, grouped as the catalog groups
 * them (Body, Brakes, Drive train, Engine…). Pick one and the drawing opens
 * in the page with its parts — each row priced and addable when the store
 * carries it — through part-diagram.js's DiagramView, the same drawing and
 * list as the product page's dialog.
 *
 * The element is <machine-viewer>, not <model-viewer>: that name belongs to
 * Google's 3D web component, which Shopify loads for 3D product media.
 *
 * The page's state is its URL — ?machine=<key>&diagram=<ipl_key> — so Back
 * works, a link shares what it shows, and the collection page's "Parts
 * diagrams" link lands on its machine's list. The machine is the URL's when
 * it names one, else the garage's current one. Picking a machine here makes
 * it current in the garage, as the homepage strip does; a machine reached
 * by link that is not in the garage shows with an offer to save it.
 *
 * Also here, on every page: the collection page's link to this page
 * ([data-model-viewer-link]), hidden until the API confirms the machine has
 * drawings — the page has nothing to show for one it lacks — with the count
 * remembered per machine beside the model match (part-diagram.js's cache).
 */
(function () {
  'use strict';

  // Tokens that keep their capitals when a title is put in sentence case.
  const ACRONYMS = new Set(['EPS', 'ECM', 'ECU', 'ATV', 'UTV', 'ASM', 'CVT', 'EFI', 'ABS', 'AWD', 'ADC', 'EBS', 'LED', 'GPS', 'USB', 'HD', 'XP', 'SP', 'LE', 'EV', 'AC', 'DC', 'PTO', 'ROPS', 'EPAS', 'ETC', 'CV', 'HVAC', 'RH', 'LH', 'TRG', 'PREM', 'ULT', 'XC', 'ES', 'SC', 'LT', 'HO', 'EXT', 'RMK', 'SKS', 'MX']);
  const NEGATIVE_TTL = 24 * 60 * 60 * 1000; // a machine the API could not match is asked again after a day

  const PD = () => window.PartDiagram;
  const upper = (s) => String(s || '').toUpperCase().replace(/\s+/g, ' ').trim();
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  /* ---- The catalog's titles ---- */

  /** "CLOSE OFF PANEL and ACCESS PANEL" → "Close off panel and access panel"; part codes and acronyms keep their case. */
  function sentence(text) {
    const words = String(text || '').replace(/,(?=\S)/g, ', ').trim().split(/\s+/).filter(Boolean);
    return words.map((word, i) => {
      const core = word.replace(/[.,;:]+$/, '');
      const keep = /\d/.test(core) || (core === core.toUpperCase() && ACRONYMS.has(core));
      let out = keep ? word : word.toLowerCase();
      if (i === 0 && !keep) out = out.charAt(0).toUpperCase() + out.slice(1);
      return out;
    }).join(' ');
  }

  /** "BODY, FRONT RACK - A25SDA57A7/E57A4 (C102613)" → group BODY, name "FRONT RACK", tail "A25SDA57A7/E57A4 (C102613)". */
  function parseTitle(title) {
    const raw = String(title || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
    const dash = raw.indexOf(' - ');
    const head = (dash === -1 ? raw : raw.slice(0, dash)).trim();
    const tail = dash === -1 ? '' : raw.slice(dash + 3).trim();
    const comma = head.indexOf(',');
    if (comma === -1) return { group: upper(head), name: '', tail };
    return { group: upper(head.slice(0, comma)), name: head.slice(comma + 1).trim(), tail };
  }

  /**
   * The API's titles are "GROUP, NAME - MODEL (CODE)". A few have no comma
   * ("STEERING POST", "ELECTRICAL BATTERY"): they join the group they start
   * with. One that starts no group ("SERVICE AND MAINTENANCE - ATV 570 …")
   * is a group of its own, its one entry named for what the sheet covers.
   */
  function assembliesOf(diagrams) {
    const list = (diagrams || []).map((d) => Object.assign(parseTitle(d.title), {
      ipl_key: String(d.ipl_key), title: String(d.title || ''), code: String(d.code || ''), parts: Number(d.pl_count) || 0,
    }));
    const groups = [...new Set(list.filter((a) => a.name).map((a) => a.group))];
    for (const a of list) {
      if (a.name) continue;
      const owner = groups.find((g) => a.group.startsWith(`${g} `));
      if (owner) {
        a.name = a.group.slice(owner.length + 1);
        a.group = owner;
      } else {
        a.name = a.tail || a.group;
        a.tail = '';
      }
    }
    // Two drawings with one name in a group (two "STEERING POST" sheets, one
    // per trim) are told apart by their tail, the model and drawing code.
    const seen = new Map();
    for (const a of list) {
      const id = `${a.group}\n${upper(a.name)}`;
      seen.set(id, (seen.get(id) || 0) + 1);
    }
    for (const a of list) a.ambiguous = seen.get(`${a.group}\n${upper(a.name)}`) > 1 && !!a.tail;
    return list;
  }

  /** The entry's name for a list or a select: sentence case, the tail added when the name alone would repeat. */
  function labelOf(a) {
    return a.ambiguous ? `${sentence(a.name)} · ${a.tail}` : sentence(a.name);
  }

  /** Groups in order of first appearance, each with its assemblies in the API's order. */
  function grouped(list) {
    const map = new Map();
    for (const a of list) {
      if (!map.has(a.group)) map.set(a.group, []);
      map.get(a.group).push(a);
    }
    return [...map.entries()].map(([group, items]) => ({ group, items }));
  }

  /** A vehicle record from a collection's handle and title: "2025 Polaris Sportsman X2 570 EPS". */
  function vehicleFromLabel(key, label) {
    const text = String(label || '').replace(/\s+/g, ' ').trim();
    const year = (String(key || '').match(/^(\d{4})-/) || [])[1] || (text.match(/^(\d{4})\s/) || [])[1] || '';
    const rest = text.replace(/^\d{4}\s+/, '');
    const make = rest.split(' ')[0] || '';
    return { key: String(key || ''), label: text, year, make, model: rest.slice(make.length).trim() };
  }

  /* ---- The page ---- */

  class MachineViewer extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.vehicle = null;
      this.machine = null;
      this.brand = null;
      this.list = [];
      this.shown = null;
      this.machineToken = 0;
      this.drawToken = 0;

      this.add = this.querySelector('[data-mv-add]');
      this.addLabel = this.add ? this.add.textContent.trim() : '';

      this.view = new (PD().DiagramView)(this.querySelector('[data-mv-drawing]'));
      this.view.onAssemblyChange = (iplKey) => this.go({ diagram: iplKey });

      this.addEventListener('click', (event) => {
        const tag = event.target.closest('[data-mv-machine]');
        if (tag) { this.pick(tag.dataset.mvMachine); return; }
        const link = event.target.closest('[data-mv-diagram]');
        if (link) {
          // A modified click (a new tab, say) is the browser's: the link is real.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          this.go({ diagram: link.dataset.mvDiagram });
          return;
        }
        if (event.target.closest('[data-mv-all]')) { event.preventDefault(); this.go({ diagram: null }); return; }
        if (event.target.closest('[data-mv-add]')) { this.toggleFinder(!this.hasAttribute('data-adding'), { focus: true }); return; }
        if (event.target.closest('[data-mv-save]') && this.vehicle) window.Garage?.add(this.vehicle);
      });
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-mv-model-select]')) return;
        if (this.vehicle) PD().chooseMachine(this.vehicle.key, event.target.value);
        this.loadMachine();
      });
      // The finder saves the machine to the garage and would go to its parts;
      // here it stays, and shows the machine's diagrams.
      this.addEventListener('fitment:selected', (event) => {
        event.preventDefault();
        this.toggleFinder(false);
        this.go({ machine: event.detail.vehicle.key, diagram: null });
      });
      document.addEventListener('garage:changed', () => {
        this.paintGarage();
        // Nothing chosen yet and a machine arrives (the modal, another tab): show it.
        if (!this.vehicle && window.Garage?.active()) this.route();
      });
      window.addEventListener('popstate', () => this.route());
      this.route();
    }

    params() { return new URLSearchParams(window.location.search); }

    /** This page's address for a state: the machine and, with one, the drawing. */
    hrefFor({ machine, diagram } = {}) {
      const p = this.params();
      const key = machine !== undefined ? machine : (p.get('machine') || (this.vehicle && this.vehicle.key) || null);
      p.delete('machine');
      p.delete('diagram');
      if (key) p.set('machine', key);
      if (diagram) p.set('diagram', diagram);
      const query = p.toString();
      return `${window.location.pathname}${query ? `?${query}` : ''}`;
    }

    go(state) {
      try { window.history.pushState({}, '', this.hrefFor(state)); } catch { /* a file: page, say */ }
      this.route();
    }

    /** A tag in the garage row: current in the garage, as the strip's "Make current", and this page's machine. */
    pick(key) {
      window.Garage?.setActive(key);
      this.go({ machine: key, diagram: null });
    }

    /** The garage's record for a key, else the taxonomy's (a machine reached by link). */
    async vehicleFor(key) {
      const saved = window.Garage ? window.Garage.vehicles.find((v) => v.key === key) : null;
      if (saved) return saved;
      if (!window.FitmentData) return null;
      try {
        await window.FitmentData.load(this.querySelector('fitment-finder')?.dataset.taxonomyUrl);
        return window.FitmentData.fromKey(key);
      } catch (err) {
        console.error('[model-viewer] taxonomy', err);
        return null;
      }
    }

    /** The URL (or the garage) says which machine; a new one loads its assemblies. */
    async route() {
      const key = this.params().get('machine') || window.Garage?.active()?.key || null;
      const vehicle = key ? await this.vehicleFor(key) : null;
      if (!vehicle || !this.vehicle || vehicle.key !== this.vehicle.key) {
        this.vehicle = vehicle;
        this.paintGarage();
        if (vehicle) this.loadMachine();
        else { this.list = []; this.machine = null; this.brand = null; }
      }
      this.show();
    }

    /** Paint the state the URL and what has loaded call for: pick a machine, its list, or a drawing. */
    show() {
      const diagram = this.params().get('diagram');
      const entry = this.vehicle && diagram ? this.list.find((a) => a.ipl_key === diagram) : null;
      const state = !this.vehicle ? 'pick' : entry ? 'drawing' : 'list';
      this.dataset.state = state;
      const empty = this.querySelector('[data-mv-empty]');
      if (empty) empty.hidden = state !== 'pick';
      const assemblies = this.querySelector('[data-mv-assemblies]');
      if (assemblies) assemblies.hidden = state !== 'list';
      const drawing = this.querySelector('[data-mv-drawing]');
      if (drawing) drawing.hidden = state !== 'drawing';
      // With nothing to pick from the finder is simply open (the section's
      // CSS, by data-state): no "Cancel" to leave behind once the garage
      // arrives — it is read after this element's script ran.
      if (entry && this.shown !== entry.ipl_key) this.showDrawing(entry);
      if (state !== 'drawing') this.shown = null;
    }

    status(text) {
      const node = this.querySelector('[data-mv-status]');
      if (node) { node.textContent = text || ''; node.hidden = !text; }
    }

    /** "Add a machine" reveals the finder under the row, and folds it away again. */
    toggleFinder(open, { focus = false } = {}) {
      this.toggleAttribute('data-adding', open);
      if (this.add) {
        this.add.setAttribute('aria-expanded', String(open));
        this.add.textContent = open ? 'Cancel' : this.addLabel;
      }
      if (open && focus) this.querySelector('[data-mv-finder] select')?.focus();
    }

    /** The garage as a row of tags, the current machine first; a machine reached by link that is not saved leads, with the offer to save it. */
    paintGarage() {
      const list = this.querySelector('[data-mv-machines]');
      if (!list) return;
      const active = window.Garage?.active() || null;
      let vehicles = window.Garage ? window.Garage.vehicles : [];
      if (active) vehicles = [active, ...vehicles.filter((v) => v.key !== active.key)];
      const chosen = this.vehicle;
      const guest = chosen && !vehicles.some((v) => v.key === chosen.key) ? chosen : null;
      const items = (guest ? [guest, ...vehicles] : vehicles).map((vehicle) => {
        const li = el('li', 'model-viewer__machine-item');
        const tag = el('button', 'model-viewer__machine', vehicle.nickname || vehicle.label);
        tag.type = 'button';
        tag.dataset.mvMachine = vehicle.key;
        tag.setAttribute('aria-pressed', String(!!chosen && chosen.key === vehicle.key));
        if (vehicle.nickname) tag.title = vehicle.label;
        li.append(tag);
        if (vehicle === guest) {
          const save = el('button', 'model-viewer__save', 'Save to my garage');
          save.type = 'button';
          save.setAttribute('data-mv-save', '');
          li.append(save);
        }
        return li;
      });
      list.replaceChildren(...items);
      this.toggleAttribute('data-has-machines', vehicles.length > 0);
    }

    paintModelLine(machine) {
      const line = this.querySelector('[data-mv-model]');
      if (line) { line.textContent = machine ? machine.name : ''; line.hidden = !machine; }
      const wrap = this.querySelector('[data-mv-models]');
      const select = this.querySelector('[data-mv-model-select]');
      if (!wrap || !select) return;
      const candidates = (machine && machine.candidates) || [];
      wrap.hidden = candidates.length < 2;
      select.replaceChildren(...candidates.map((c) => {
        const option = el('option', '', c.name);
        option.value = c.model_key;
        option.selected = c.model_key === machine.model_key;
        return option;
      }));
    }

    /** The machine's assemblies: which API model it is, then its drawings. */
    async loadMachine() {
      const vehicle = this.vehicle;
      const token = ++this.machineToken;
      this.list = [];
      this.machine = null;
      this.brand = null;
      const name = this.querySelector('[data-mv-machine-name]');
      if (name) name.textContent = vehicle.nickname || vehicle.label;
      const shop = this.querySelector('[data-mv-shop]');
      if (shop) shop.href = `/collections/${encodeURIComponent(vehicle.key)}`;
      this.paintModelLine(null);
      this.paintIndex();
      this.paintSwitch();

      const brand = PD().brandOf(vehicle.make);
      if (!window.Fitment) { this.status('The diagram service is not available right now.'); this.show(); return; }
      if (!brand) { this.status(`Diagrams are not available for ${vehicle.make || 'this brand'} machines yet.`); this.show(); return; }
      this.status('Finding the diagrams for this machine…');
      try {
        const machine = await PD().machineFor(brand, vehicle);
        if (token !== this.machineToken) return;
        if (!machine) {
          PD().remember(vehicle.key, { diagrams: 0, checked: Date.now() });
          this.status(`We couldn't match your ${vehicle.label} to a diagram model yet.`);
          this.show();
          return;
        }
        this.paintModelLine(machine);
        const model = await window.Fitment.model(brand, machine.model_key);
        if (token !== this.machineToken) return;
        this.brand = brand;
        this.machine = machine;
        this.list = assembliesOf(model && model.diagrams);
        PD().remember(vehicle.key, { diagrams: this.list.length });
        this.status(this.list.length ? '' : 'No diagrams for this machine yet.');
        this.paintIndex();
        this.paintSwitch();
      } catch (err) {
        console.error('[model-viewer]', err);
        if (token !== this.machineToken) return;
        this.status('The diagram service did not answer. Try again in a moment.');
      }
      this.show();
    }

    /** The groups, each with its assemblies as links to this page's address for them. */
    paintIndex() {
      const index = this.querySelector('[data-mv-index]');
      if (!index) return;
      index.replaceChildren(...grouped(this.list).map(({ group, items }) => {
        const section = el('section', 'model-viewer__group');
        section.append(el('h3', 'eyebrow model-viewer__group-title', sentence(group)));
        const ul = el('ul', 'model-viewer__list');
        for (const a of items) {
          const li = el('li');
          const link = el('a', 'model-viewer__link', labelOf(a));
          link.href = this.hrefFor({ diagram: a.ipl_key });
          link.dataset.mvDiagram = a.ipl_key;
          link.title = a.title;
          li.append(link);
          ul.append(li);
        }
        section.append(ul);
        return section;
      }));
    }

    /** The drawing view's switch: the same list as a select, grouped. */
    paintSwitch() {
      const select = this.querySelector('[data-diagram-assembly-select]');
      if (select) {
        select.replaceChildren(...grouped(this.list).map(({ group, items }) => {
          const optgroup = el('optgroup');
          optgroup.label = sentence(group);
          for (const a of items) {
            const option = el('option', '', labelOf(a));
            option.value = a.ipl_key;
            optgroup.append(option);
          }
          return optgroup;
        }));
      }
      const all = this.querySelector('[data-mv-all]');
      if (all) all.href = this.hrefFor({ diagram: null });
    }

    async showDrawing(entry) {
      this.shown = entry.ipl_key;
      const token = ++this.drawToken;
      const group = this.querySelector('[data-mv-group]');
      if (group) group.textContent = sentence(entry.group);
      const title = this.querySelector('[data-diagram-title]');
      if (title) title.textContent = sentence(entry.name);
      const code = this.querySelector('[data-mv-code]');
      if (code) { code.textContent = entry.tail; code.hidden = !entry.tail; }
      const select = this.querySelector('[data-diagram-assembly-select]');
      if (select) select.value = entry.ipl_key;
      this.view.clear();
      this.view.status('Loading the drawing…');
      // From the list the drawing stands where the list stood; from further
      // down the page, come back up to it.
      const drawing = this.querySelector('[data-mv-drawing]');
      if (drawing && drawing.getBoundingClientRect().top < 0) drawing.scrollIntoView({ block: 'start' });
      try {
        const [data, store] = await Promise.all([
          window.Fitment.diagram(this.brand, entry.ipl_key),
          this.view.storeMap(this.vehicle.key),
        ]);
        if (token !== this.drawToken) return;
        this.view.status('');
        this.view.paintStage(data);
        this.view.paintRows(data, store, '');
      } catch (err) {
        console.error('[model-viewer]', err);
        if (token !== this.drawToken) return;
        this.view.status('The drawing did not load. Try again in a moment.');
      }
    }
  }

  if (!customElements.get('machine-viewer')) customElements.define('machine-viewer', MachineViewer);

  /* ---- The collection page's link ---- */

  /** How many drawings the API has for a machine, remembered beside its model match. */
  async function diagramCount(key, label) {
    const hit = PD().cached(key);
    if (hit && typeof hit.diagrams === 'number' && (hit.model_key || Date.now() - (hit.checked || 0) < NEGATIVE_TTL)) return hit.diagrams;
    const saved = window.Garage ? window.Garage.vehicles.find((v) => v.key === key) : null;
    const vehicle = saved || vehicleFromLabel(key, label);
    const brand = PD().brandOf(vehicle.make);
    if (!brand || !window.Fitment) return 0;
    const machine = await PD().machineFor(brand, vehicle);
    if (!machine) {
      PD().remember(key, { diagrams: 0, checked: Date.now() });
      return 0;
    }
    const model = await window.Fitment.model(brand, machine.model_key);
    const count = ((model && model.diagrams) || []).length;
    PD().remember(key, { diagrams: count });
    return count;
  }

  function revealLinks() {
    for (const link of document.querySelectorAll('[data-model-viewer-link][data-vehicle-key]')) {
      diagramCount(link.dataset.vehicleKey, link.dataset.vehicleLabel)
        .then((count) => { link.hidden = !(count > 0); })
        .catch((err) => { console.error('[model-viewer] link', err); link.hidden = true; });
    }
  }

  function boot() {
    revealLinks();
    // The collection page re-renders its section on a filter or sort change
    // (collection.js), and the link comes back hidden.
    const host = document.querySelector('facet-filters[data-collection]');
    if (host && window.MutationObserver) new MutationObserver(() => revealLinks()).observe(host, { childList: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  window.ModelViewer = { sentence, parseTitle, assembliesOf, grouped, labelOf, vehicleFromLabel, diagramCount, revealLinks };
})();
